/* eslint-disable no-sequences */
/* eslint-disable react/no-array-index-key */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { getPageLinkByPageType, useGetLink } from "@tCentro/Store/CustomLinksSelectors";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OfferingModel {
    fields: {
        image: string;
        displayImage: boolean;
        expertName: string;
        linkTitle: string;
        linkURL: LinkFieldValue;
        expertTime: string;
    };
}

const enum fields {
    title = "title",
    subTitle = "subTitle",
    description = "description",
    changeImage = "changeImage",
    startDate = "startDate",
    offerings = "offerings",
    contactNumber = "contactNumber",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subTitle]: string;
        [fields.description]: string;
        [fields.changeImage]: string;
        [fields.startDate]: string;
        [fields.contactNumber]: number;
        [fields.buttonLink]: LinkFieldValue;
        [fields.offerings]: OfferingModel[];
    };
}

export interface LookingExpertPDPStyles {
    container?: InjectableCss;
    titleGridItem?: InjectableCss;
    subTitle?: InjectableCss;
    imageGridItem?: InjectableCss;
    imageAndTitleSection?: InjectableCss;
    titileDescriptionGridItem?: InjectableCss;
    scheduleLinkStyle?: InjectableCss;
    expertTitle?: TypographyPresentationProps;
    expertName?: TypographyPresentationProps;
    expertTime?: TypographyPresentationProps;
    image?: LazyImageProps;
    chatButtonWrapper?: InjectableCss;
    numberStyles?: InjectableCss;
    buttonStyle?: ButtonPresentationProps;
}

export const lookingExpertPDPStyles: LookingExpertPDPStyles = {
    titleGridItem: {
        css: css`
            display: flex;
            flex: 1;
            flex-direction: column;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    titileDescriptionGridItem: {
        css: css`
            flex-direction: column;
            max-height: 120px !important;
        `,
    },
    imageAndTitleSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            justify-content: start;
            div {
                max-height: 120px !important;
            }
        `,
    },
    expertTitle: {
        css: css`
            color: ${getProp("theme.typography.h5.color")};
            font-size: ${TYPOGRAPHY.size.xxlarge};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 40px;
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                            line-height: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}@media (min-width: 992px) and (max-width: 1244px) {
                font-size: ${TYPOGRAPHY.size.largest};
            } ;
        `,
    },
    subTitle: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.large};
            color: ${getProp("theme.typography.h6.color")};
            line-height: 24px;
            margin-bottom: 24px;
        `,
    },

    expertName: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-bottom: 12px;
        `,
    },
    expertTime: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 16px;
            margin-bottom: 12px;
        `,
    },
    chatButtonWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    scheduleLinkStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            border: 2px solid ${COLORS.secondary.base};
            border-radius: 24px;
            padding: 8px 12px;
            height: 32px;
            color: ${COLORS.secondary.base} !important;

            span {
                color: ${COLORS.secondary.base} !important;
            }
        `,
    },
    image: {
        css: css`
            aspect-ratio: 1/1;
            object-fit: contain;
            max-width: 120px !important;
            max-height: 120px !important;
            height: auto;
            img {
                width: 120px !important;
                height: 120px !important;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-height: 72px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    numberStyles: {
        css: css`
            color: ${getColor("text.link")};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            margin-bottom: 8px;
        `,
    },
    buttonStyle: {
        css: css`
            padding: 8px 16px;
            border-radius: 16px;
            max-width: 149px !important;
            min-height: 32px !important;
            border: 0px;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null], "max")}
        `,
    },
};

const mapStateToProps: any = (state: ApplicationState & CustomApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.offerings.map(card => getLink(state, card.fields.linkURL)),
        isExpanded: state.t_components.overViewPdpBlock?.overViewPdpBlockisExpanded,
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = lookingExpertPDPStyles;
const CENCentroRep: React.FC<Props> = ({ fields, pageLink, isExpanded }) => {
    const container = {
        css: css`
            padding: 40px 40px 38px 40px;
            background: ${COLORS.background.grayShade};
            height: ${isExpanded ? "max-content" : "100%"};
            display: flex;
            justify-content: center;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px 20px 20px 20px;
                    `,
                    css``,
                    css`
                        padding: 40px 40px 60px 40px;
                    `,
                    null,
                    null,
                ])} @media (min-width: 992px) and (max-width: 1054px) {
                padding: 40px 25px 60px 25px;
            }
            @media (min-width: 1198px) and (max-width: 1233px) {
                padding: 40px 40px 15px 40px;
            }
        `,
    };
    const [chatLink, setChatLink] = useState<any>();
    const [offerringsUpdate, setOfferingData] = useState<any>();
    useEffect(() => {
        const changeImageDaysNumber = Number(fields.changeImage);
        const selectedStartDate = new Date(fields.startDate);
        const todayDate = new Date();

        if (todayDate >= selectedStartDate) {
            const offeringsArrLen = fields.offerings.length;
            const diffDays =
                Math.floor(
                    (Date.UTC(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate()) -
                        Date.UTC(
                            selectedStartDate.getFullYear(),
                            selectedStartDate.getMonth(),
                            selectedStartDate.getDate(),
                        )) /
                        (1000 * 60 * 60 * 24),
                ) / changeImageDaysNumber;
            const diffDaysRound = Math.ceil(diffDays);

            if (offeringsArrLen > diffDaysRound) {
                setOfferingData(fields.offerings[diffDaysRound]);
                setChatLink(pageLink[diffDaysRound]?.url);
            } else if (offeringsArrLen < diffDaysRound) {
                const expertLengthDiff = diffDaysRound % offeringsArrLen;
                setOfferingData(fields.offerings[expertLengthDiff]), setChatLink(pageLink[expertLengthDiff]?.url);
            } else if (offeringsArrLen === diffDaysRound) {
                setOfferingData(fields.offerings[offeringsArrLen - 1]);
                setChatLink(pageLink[offeringsArrLen - 1]?.url);
            }
        }
    }, [fields]);
    const buttonRedirectLink = useGetLink(fields.buttonLink);
    const handleClick = () => {
        if (buttonRedirectLink.url) {
            window.location.href = buttonRedirectLink.url;
        }
    };
    return (
        <>
            {offerringsUpdate && (
                <StyledWrapper {...container}>
                    <StyledWrapper {...styles.titileDescriptionGridItem}>
                        {fields.title && (
                            <Typography as="h5" {...styles.expertTitle}>
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.titileDescriptionGridItem}>
                        {fields.subTitle && (
                            <Typography as="h6" {...styles.subTitle}>
                                {parse(transformContentHtml(fields.subTitle), parserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>

                    <StyledWrapper>
                        <StyledWrapper
                            {...styles.imageAndTitleSection}
                            style={{ gap: offerringsUpdate.fields.displayImage ? "16px" : "0px" }}
                        >
                            <StyledWrapper {...styles.imageGridItem}>
                                {offerringsUpdate.fields.displayImage && (
                                    <LazyImage {...styles.image} altText={offerringsUpdate.fields.expertName} src={offerringsUpdate.fields.image} />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.titleGridItem}>
                                <Typography as="p" {...styles.expertName}>
                                    {offerringsUpdate.fields.expertName}
                                </Typography>
                                <StyledWrapper {...styles.chatButtonWrapper}>
                                    <Typography {...styles.numberStyles}>{fields.contactNumber}</Typography>
                                </StyledWrapper>
                                <Typography as="p" {...styles.expertTime}>
                                    {offerringsUpdate.fields.expertTime}
                                </Typography>
                                <Button {...styles.buttonStyle} onClick={handleClick}>
                                    {translate("Contact My Rep")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENCentroRep),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.subTitle,
                displayName: "subTitle",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
            },

            {
                name: fields.changeImage,
                displayName: "After how many days image should change",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.startDate,
                displayName: "Start date",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
            },
            {
                name: fields.contactNumber,
                displayName: "Contact Number",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Redirect",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                editorTemplate: "LinkField",
            },
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "displayImage",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },

                    {
                        name: "expertName",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkURL",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "expertTime",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
